/**
 * @barmetvault/shared — User types
 *
 * Canonical user-related types shared between API and web frontend.
 */

// ─── Enums ─────────────────────────────────────────────────────────────────

export enum UserStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  REVOKED = 'REVOKED',
}

export enum MfaType {
  NONE = 'NONE',
  TOTP = 'TOTP',
  WEBAUTHN = 'WEBAUTHN',
}

export enum UserRole {
  OWNER = 'OWNER',
  SECURITY_ADMIN = 'SECURITY_ADMIN',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
  AUDITOR = 'AUDITOR',
  SERVICE_ACCOUNT = 'SERVICE_ACCOUNT',
}

// ─── Interfaces ────────────────────────────────────────────────────────────

export interface User {
  id: string;
  orgId: string;
  email: string;
  status: UserStatus;
  mfaType: MfaType;
  role: UserRole;
  createdAt: string;
  lastLoginAt: string | null;
}

/**
 * Safe subset of User that excludes sensitive fields.
 * Use this when returning user data to clients that don't
 * need full user details.
 */
export type UserProfile = Pick<
  User,
  'id' | 'email' | 'status' | 'role' | 'createdAt'
>;

/**
 * Registration payload sent from client to server.
 * The master password NEVER travels — only derived artifacts.
 */
export interface RegisterPayload {
  email: string;
  masterPwdHash: string;        // base64 — Argon2id-derived auth hash
  kdfSalt: string;              // base64 — random salt for master key derivation
  publicKeyEd25519: string;     // base64 — Ed25519 public key
  publicKeyX25519: string;      // base64 — X25519 public key
  privateKeysEnc: string;       // base64 — encrypted private keys (with KEK from master key)
  privateKeysNonce: string;     // base64 — nonce for private key encryption
  kekSalt: string;              // base64 — salt used to derive KEK
}
